import { useEffect, useRef, useState, useMemo } from 'react';
import styles from './LegalCases.module.scss';
import { fetchLegalCasesData } from '../../../../../store/dashboard/tables/tables_apiHelpers';
import { getTimeStamp } from '../../../../../utils/dateUtils';
import { HEADERS, LegalCasesData, getHeadersFiledAgainst, getHeadersFiledBy } from './legalCasesConstants';
import greenTick from '../../../../../assets/green-tick.svg';
import clsx from 'clsx';
import {
    convertToCapitalizeUnderscore,
    convertToFirstLetterCapital,
    decodeHtmlEntities,
    getSubdomain,
    normalizeNameKey,
} from '../../../../../utils/utils';
import generating from '../../../../../assets/audited-financials-icon.svg';
import loader from '../../../../../assets/aica-loader.svg';
import { DATA_FETCH_STATUS } from '../../../../../store/dashboard/constants';
import { downloadIconDataRoom, downloadIconDataRoomHovered } from '../../../../../assets/hostedassets';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFocusRowFromUrlParam } from '../../../../../utils/customHooks';
import { getFolderStructure } from '../../../../../store/dataRoom/apiHelper';
import { toast } from 'react-toastify';
import RiskIndicators from '../../../../RiskIndicators/RiskIndicators';
import A4Page from '../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../store/pdfReport/pdfReport_constants';
import AicaOverview from '../../../../../common/AicaOverview/AicaOverview';
import { TabModule } from '../../../../../utils/constants/commonConstants';
import { removeTooltipContent, setTooltipContent } from '../../../../../store/tooltip/tooltip_actions';
import { userBlack, companyBlack } from '../../../../../assets/hostedassets';
import DownloadIconDisabled from '../../../../../assets/Svg/DownloadIconDisabled';
import { AICA_LEGAL_CASE_DOWNLOAD, AICA_LEGAL_CASE_DOWNLOAD_ALL } from '../../../../../utils/constants/mixpanelEvents/investorEvents';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import HeaderBodyBox from '../../../../../common/HeaderBodyBox/HeaderBodyBox';
import dayjs from 'dayjs';
import AskAicaQuestionBadge from '../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';

const STATUS = {
    PENDING: { value: 'Pending', order: 0 },
    DISPOSED: { value: 'Disposed', order: 1 },
    NOT_AVAILABLE: { value: 'Not Available', order: 2 },
    NO_CASES: { value: 'NO_CASES', order: 3 },
};

function LegalCases({
    assesseeOrgId,
    companyName,
    initialiseHeadlinePanelPolling,
    isPDFReport = false,
}: {
    assesseeOrgId: string;
    companyName: string;
    initialiseHeadlinePanelPolling?: Function;
    isPDFReport?: boolean;
}) {
    const dispatch = useAppDispatch();
    const [legalCasesData, setLegalCasesData] = useState<null | LegalCasesData>();
    const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);
    const lastFetchedDate = legalCasesData?.lastFetchedDate || null;
    let pollingIntervalRef: any = useRef(null);
    const userData = useAppSelector((state) => state.user.userData);
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const params = useParams();
    const [searchParams] = useSearchParams();
    // 'regulatoryChecks' targets the Risk Indicators (AML/PEP) section; default lands on Legal Cases rows.
    const focusSection = searchParams.get('section') ?? '';

    const addPlaceholderEntries = (casesData: any[]) => {
        const allEntityNames = casesData.map((caseRow) => caseRow.name);
        const entitiesWithCases = casesData.filter((caseRow) => caseRow.cases && caseRow.cases.length > 0).map((caseRow) => caseRow.name);
        const entitiesWithoutCases = allEntityNames.filter((name) => !entitiesWithCases.includes(name));

        return [
            ...casesData,
            ...entitiesWithoutCases.map((name) => ({
                name,
                cases: [{ groupName: name, status: STATUS.NO_CASES.value }],
            })),
        ];
    };

    const flattenAndLimitCases = (casesData: any[], isPDFReport: boolean) => {
        return casesData.flatMap((caseRow) =>
            (isPDFReport ? caseRow.cases.slice(0, 10) : caseRow.cases).map((singleCase) => ({
                ...singleCase,
                groupName: caseRow.name,
            })),
        );
    };

    const sortCasesByStatusAndGroup = (cases: any[]) => {
        return cases.sort((a, b) => {
            const statusA = Object.values(STATUS).find((s) => s.value === a.status)?.order ?? 4;
            const statusB = Object.values(STATUS).find((s) => s.value === b.status)?.order ?? 4;
            if (statusA !== statusB) return statusA - statusB;

            if (a.groupName === 'Company' && b.groupName !== 'Company') return -1;
            if (a.groupName !== 'Company' && b.groupName === 'Company') return 1;
            return 0;
        });
    };

    const getFlatSortedCases = (casesData: any[]): any[] => {
        if (!casesData || !Array.isArray(casesData)) return [];
        const casesWithPlaceholders = addPlaceholderEntries(casesData);
        const flattenedCases = flattenAndLimitCases(casesWithPlaceholders, isPDFReport);
        return sortCasesByStatusAndGroup(flattenedCases);
    };

    const legalCasesByCompanyOrDirector = useMemo(
        () => getFlatSortedCases(legalCasesData?.legalCasesFiledByCompanyOrDirector || []),
        [legalCasesData?.legalCasesFiledByCompanyOrDirector, isPDFReport],
    );

    const legalCasesAgainstCompanyOrDirector = useMemo(
        () => getFlatSortedCases(legalCasesData?.legalCasesFiledAgainstCompanyOrDirector || []),
        [legalCasesData?.legalCasesFiledAgainstCompanyOrDirector, isPDFReport],
    );

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (modulePollingList.includes(TabModule.LEGAL_CHECKS)) {
            fetchData();
        }
    }, [modulePollingList]);

    // Deep-link from Management Profile: choose section based on which column was clicked.
    // Regulatory Checks → Risk Indicators (AML/PEP) rows; Legal Cases (default) → Legal Cases rows.
    useFocusRowFromUrlParam({
        ready: !isPDFReport && !!legalCasesData,
        paramKey: 'directorName',
        dataAttr: focusSection === 'regulatoryChecks' ? 'ri-director' : 'lc-director',
    });

    useEffect(() => {
        if (legalCasesData?.status && legalCasesData?.status === DATA_FETCH_STATUS.PROCESSING) {
            initialiseHeadlinePanelPolling?.();
            pollingIntervalRef.current = setInterval(() => {
                fetchData();
            }, 5000);
        } else {
            clearInterval(pollingIntervalRef?.current);
        }

        return () => {
            clearInterval(pollingIntervalRef?.current);
        };
    }, [legalCasesData?.status]);

    const fetchData = async () => {
        await fetchLegalCasesData(
            {
                assesseeOrgId: assesseeOrgId,
                requestId: getTimeStamp(),
            },
            {
                onSuccess: (data) => {
                    setLegalCasesData(data);
                },
                onError: () => {},
            },
        );
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case STATUS.DISPOSED.value:
                return 'CLOSED';
            case STATUS.PENDING.value:
                return 'OPEN';
            case STATUS.NOT_AVAILABLE.value:
                return '-';
        }
    };

    const getDisplayDate = (singleCase: any, forExcel: boolean = false) => {
        const { decisionDate, nextHearingDate } = singleCase || {};

        const { date, message } = decisionDate?.trim()
            ? { date: decisionDate, message: 'Last Judgement' }
            : nextHearingDate?.trim()
            ? { date: nextHearingDate, message: 'Next Hearing' }
            : { date: '', message: '' };

        const formattedDate = date && dayjs(date).isValid() ? dayjs(date).format('DD MMM YYYY') : forExcel ? '' : '-';

        if (forExcel) {
            return message ? `${message}: ${formattedDate}` : formattedDate;
        }

        return (
            <div className={styles.DateContainer}>
                {message && <span className={styles.DateMessage}>{message}</span>}
                <span className={styles.Date}>{formattedDate}</span>
            </div>
        );
    };

    const downloadCaseFunction = async (docIds, name, caseType) => {
        const validDocIds = docIds.filter((docId) => docId && docId.trim && docId.trim() !== '');
        const fileNodes = validDocIds.map((docId) => ({
            name: null,
            type: 'file',
            innerFiles: [],
            docId: docId,
            updatedAt: Date.now(),
        }));
        const payload = {
            parentPath: 'CaseFiles',
            requestId: getTimeStamp(),
            fileNodes: fileNodes,
            downloadByOrgId: userData?.investorOrgId,
        };
        Mixpanel.track(AICA_LEGAL_CASE_DOWNLOAD, {
            parentId: userData?.investorOrgId,
            websiteUrl: window.location.href,
            userId: userData?.userId,
            userRole: userData?.accountType,
            caseType: caseType,
            companyOrgId: params.id,
        });
        const res = await getFolderStructure(payload);
        if (res) {
            let blob = new Blob([res], { type: 'octet/stream' });
            let fileName = `${name}.zip`;
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
            toast.success('Folder Downloaded Successfully');
        } else {
            toast.error('Error in fetching folder');
        }
    };

    const reorderNames = (text: string, groupName: string): string => {
        if (!text || !groupName) return text || '';
        if (!text.trim() || !groupName.trim()) return text;

        const nameParts = groupName.toLowerCase().split(/\s+/);
        const originalParts = text.split(',').map((part) => part.trim());
        const validParts = originalParts.filter((part) => part.length > 0);

        const matching: string[] = [];
        const nonMatching: string[] = [];

        validParts.forEach((part) => {
            const lowerPart = part.toLowerCase();
            if (nameParts.some((name) => lowerPart.includes(name))) {
                matching.push(part);
            } else {
                nonMatching.push(part);
            }
        });

        return [...matching, ...nonMatching].join(', ');
    };

    const createWorksheetHeaders = (worksheet: ExcelJS.Worksheet, companyName: string, titleText: string) => {
        worksheet.mergeCells('A1:G1');
        worksheet.getCell('A1').value = companyName;
        worksheet.getCell('A1').font = { bold: true, size: 14 };

        worksheet.mergeCells('A2:G2');
        worksheet.getCell('A2').value = titleText;
        worksheet.getCell('A2').font = { bold: true };

        worksheet.addRow([]);
    };

    const addHeaderRow = (worksheet: ExcelJS.Worksheet, headers: string[]) => {
        const headerRow = worksheet.addRow(headers);
        headerRow.height = 25;
        headerRow.eachCell((cell) => {
            cell.font = { size: 11, bold: true };
            cell.alignment = { indent: 1, vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD8EAD2' },
            };
        });
    };

    const addDataRows = (
        worksheet: ExcelJS.Worksheet,
        cases: any[],
        companyName: string,
        getStatusText: Function,
        reorderNames: Function,
        decodeHtmlEntities: Function,
    ) => {
        const dataRows = cases.map((singleCase) => {
            const groupName = singleCase.groupName === 'Company' ? companyName : singleCase.groupName;
            return [
                getStatusText(singleCase.status) || '',
                singleCase.caseNum || '',
                singleCase.court || '',
                reorderNames(decodeHtmlEntities(singleCase.filedAgainst), groupName) || '',
                reorderNames(decodeHtmlEntities(singleCase.filedBy), groupName) || '',
                singleCase.caseCategory || '',
                getDisplayDate(singleCase, true) || '',
            ];
        });

        dataRows.forEach((rowData) => {
            const row = worksheet.addRow(rowData);
            row.height = 25;
            row.eachCell((cell) => {
                cell.font = { size: 11 };
                cell.alignment = { vertical: 'middle', indent: 1 };
            });
        });
    };

    const downloadExcel = async () => {
        const workbook = new ExcelJS.Workbook();

        const headers = ['Case Status', 'Case No.', 'Court', 'Filed Against', 'Filed By', 'Case Category', 'Date'];
        const columnWidths = [15, 30, 35, 50, 50, 35, 20];

        const addSheet = (sheetName, cases, titleText) => {
            const worksheet = workbook.addWorksheet(sheetName);
            createWorksheetHeaders(worksheet, companyName, titleText);
            addHeaderRow(worksheet, headers);
            addDataRows(worksheet, cases, companyName, getStatusText, reorderNames, decodeHtmlEntities);

            worksheet.columns.forEach((col, i) => {
                col.width = columnWidths[i] || 20;
            });
        };

        addSheet('Filed Against Cases', legalCasesAgainstCompanyOrDirector, 'Legal cases filed against Company / Director');
        addSheet('Filed By Cases', legalCasesByCompanyOrDirector, 'Legal cases filed by Company / Director');

        let buffer;
        try {
            buffer = await workbook.xlsx.writeBuffer();
        } catch (e) {
            toast.error('Failed to generate Excel file');
            return;
        }

        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(blob, `Legal_Cases_${convertToCapitalizeUnderscore(companyName)}.xlsx`);

        const legalCasesByCount = legalCasesByCompanyOrDirector.filter((singleCase) => singleCase.status !== STATUS.NO_CASES.value).length;
        const legalCasesAgainstCount = legalCasesAgainstCompanyOrDirector.filter(
            (singleCase) => singleCase.status !== STATUS.NO_CASES.value,
        ).length;

        Mixpanel.track(AICA_LEGAL_CASE_DOWNLOAD_ALL, {
            userRole: userData?.accountType,
            pnoPlatform: getSubdomain(),
            userId: userData?.userId,
            currentUrl: window.location.href,
            legalCasesByCount,
            legalCasesAgainstCount,
            organizationId: params.id,
        });
    };

    const hasValidDocuments = (singleCase) => {
        return (
            Array.isArray(singleCase?.[HEADERS.REPORTS]) &&
            singleCase?.[HEADERS.REPORTS]?.length > 0 &&
            singleCase?.[HEADERS.REPORTS].some((docId) => docId && docId.trim && docId.trim() !== '')
        );
    };

    const getCaseDetails = (singleCase, uniqueKey) => {
        const locationDetailsArray = [singleCase?.court, singleCase?.district, singleCase?.state].filter(
            (value) => value && value.trim && value.trim() !== '',
        );

        return (
            <div className={styles.CaseDetails}>
                <div className={styles.CaseDetailsLeft}>
                    <div
                        className={clsx({
                            [styles.Status]: true,
                            [styles.Status_Open]: singleCase?.status === 'Pending',
                            [styles.Status_Closed]: singleCase?.status === 'Disposed',
                        })}
                    >
                        {getStatusText(singleCase?.status)}
                    </div>
                    <div className={styles.CaseDetailsText}>
                        <div>{singleCase?.caseNum || '-'}</div>
                        <div>{locationDetailsArray.join(', ') || '-'}</div>
                    </div>
                </div>
            </div>
        );
    };

    const renderRiskIndicators = () => <RiskIndicators isPDFReport={isPDFReport} />;

    const getRowItemContent = (headerKey: string, legalCase, singleCase, uniqueKey: string, sectionType: 'filed_against' | 'filed_by') => {
        switch (headerKey) {
            case HEADERS.CASE_DETAILS:
                return getCaseDetails(singleCase, uniqueKey);
            case HEADERS.REPORTS:
                return (
                    <div className={styles.CaseDetailsRight}>
                        <span
                            className={styles.DownloadReport}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!hasValidDocuments(singleCase)) return;
                                downloadCaseFunction(
                                    singleCase?.[HEADERS.REPORTS],
                                    singleCase?.[HEADERS.CASE_NUM],
                                    singleCase?.[HEADERS.CASE_CATEGORY],
                                );
                            }}
                            onMouseEnter={() => setHoveredIndex(uniqueKey)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            style={{
                                cursor: hasValidDocuments(singleCase) ? 'pointer' : 'not-allowed',
                            }}
                        >
                            {hasValidDocuments(singleCase) ? (
                                <img src={downloadIconDataRoomHovered} alt={'icon'} />
                            ) : (
                                <DownloadIconDisabled width="16px" height="16px" />
                            )}
                        </span>
                    </div>
                );
            case HEADERS.DATE:
                return (
                    <span
                        style={{
                            padding: isPDFReport ? '10px 0' : '',
                        }}
                    >
                        {getDisplayDate(singleCase)}
                    </span>
                );
            case HEADERS.FILED_AGAINST:
            case HEADERS.FILED_BY:
                if (
                    (headerKey === HEADERS.FILED_AGAINST && sectionType === 'filed_against') ||
                    (headerKey === HEADERS.FILED_BY && sectionType === 'filed_by')
                ) {
                    const nameToShow =
                        (singleCase?.filedBy?.includes('*') || singleCase?.filedAgainst?.includes('*') ? '*' : '') +
                        convertToFirstLetterCapital(legalCase.name === 'Company' ? companyName : legalCase.name);
                    const text = convertToFirstLetterCapital(decodeHtmlEntities(singleCase?.[headerKey]) || '-')?.replace(nameToShow, '');
                    let values = text.split(',').map((value) => value.trim());
                    return (
                        <span className={styles.NamePill}>
                            <img src={legalCase.name === 'Company' ? companyBlack : userBlack} height={'14px'} />
                            {nameToShow}
                            {values.length > 1 && !isPDFReport && (
                                <span
                                    className={styles.MoreText}
                                    onMouseEnter={(e) => {
                                        dispatch(
                                            setTooltipContent({
                                                content: values?.filter?.((val) => val).join(', '),
                                                target: e.currentTarget,
                                                placement: 'top',
                                            }),
                                        );
                                    }}
                                    onMouseLeave={() => removeTooltipContent(dispatch)}
                                >
                                    {' '}
                                    +{values.length - 1} more
                                </span>
                            )}
                        </span>
                    );
                }
            default:
                return (
                    <span
                        style={{
                            padding: isPDFReport ? '10px 0' : '',
                        }}
                        title={convertToFirstLetterCapital(decodeHtmlEntities(singleCase?.[headerKey]) || '-')}
                    >
                        {convertToFirstLetterCapital(decodeHtmlEntities(singleCase?.[headerKey]) || '-')}
                    </span>
                );
        }
    };

    const renderLegalCasesAgainstCompanyOrDirector = () => {
        const headerRight = (
            <div className={styles.HeaderControls}>
                {lastFetchedDate != null ? <div className={styles.UpdateDate}>Last Updated on {lastFetchedDate}</div> : null}
                <div className={styles.DownloadAllReport} onClick={downloadExcel}>
                    <img src={downloadIconDataRoomHovered} alt="icon" />
                    Download All
                </div>
            </div>
        );

        const tableContent = (
            <>
                <div className={styles.TableWrapper}>
                    <table className={styles.Table}>
                        <thead className={styles.TableHead}>
                            <tr className={styles.TableHeadRow}>
                                {getHeadersFiledAgainst(isPDFReport).map((header, i) => (
                                    <th
                                        key={header.key}
                                        className={clsx(styles.TableHeadRowItem, {
                                            [styles.TableHeadRowItem_CaseDetails]: header.key === HEADERS.CASE_DETAILS,
                                            [styles.TableHeadRowItem_FiledAgainst]: header.key === HEADERS.FILED_AGAINST,
                                            [styles.TableHeadRowItem_FiledBy]: header.key === HEADERS.FILED_BY,
                                            [styles.TableHeadRowItem_CaseType]: header.key === HEADERS.CASE_CATEGORY,
                                            [styles.TableHeadRowItem_Date]: header.key === HEADERS.DATE,
                                            [styles.TableHeadRowItem_Report]: header.key === HEADERS.REPORTS,
                                            [styles.TableHeadRowItem_PDFReport]: isPDFReport,
                                        })}
                                    >
                                        {header.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className={styles.TableBody} style={{ fontSize: isPDFReport ? '11px' : '' }}>
                            {legalCasesAgainstCompanyOrDirector.length ? (
                                legalCasesAgainstCompanyOrDirector.map((singleCase, idx) => {
                                    const uniqueKey = `${idx}-filed-against`;
                                    const legalCase = { name: singleCase.groupName };
                                    if (singleCase.status === STATUS.NO_CASES.value) {
                                        return (
                                            <tr className={styles.TableBodyRow} key={uniqueKey}>
                                                <td
                                                    colSpan={getHeadersFiledAgainst(isPDFReport).length}
                                                    className={styles.TableBodyRowItem}
                                                >
                                                    <div className={styles.NoCases}>
                                                        <img src={greenTick} height={'16px'} />
                                                        No Legal Cases filed against{' '}
                                                        {legalCase.name === 'Company'
                                                            ? convertToFirstLetterCapital(companyName)
                                                            : convertToFirstLetterCapital(legalCase.name)}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                    return (
                                        <tr
                                            className={styles.TableBodyRow}
                                            key={uniqueKey}
                                            data-lc-director={normalizeNameKey(singleCase.groupName)}
                                        >
                                            {getHeadersFiledAgainst(isPDFReport)?.map((header, i) => (
                                                <td
                                                    key={header.key}
                                                    className={clsx(styles.TableBodyRowItem, {
                                                        [styles.TableBodyRowItem_PDFReport]: isPDFReport,
                                                    })}
                                                >
                                                    {getRowItemContent(header?.key, legalCase, singleCase, uniqueKey, 'filed_against')}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr className={styles.TableBodyRow}>
                                    <td
                                        colSpan={getHeadersFiledAgainst(isPDFReport).length}
                                        className={clsx(styles.TableBodyRowItem, styles.Nodata)}
                                    >
                                        No data available to show
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={styles.Note}>
                    <b>*Note :</b> The cases listed above are based on an AI match of the company's legal name and its directors (please
                    verify names).
                </div>
            </>
        );

        return (
            <div className={clsx(styles.Container, { [styles.PDFReportContainer]: isPDFReport })}>
                <HeaderBodyBox title="Legal cases filed against Company / Director" headerRight={headerRight} isPDFReport={isPDFReport}>
                    {tableContent}
                </HeaderBodyBox>
            </div>
        );
    };

    const renderLegalCasesByCompanyOrDirector = () => {
        const headerRight = (
            <div className={styles.HeaderControls}>
                {lastFetchedDate != null ? <div className={styles.UpdateDate}>Last Updated on {lastFetchedDate}</div> : null}
                <div className={styles.DownloadAllReport} onClick={downloadExcel}>
                    <img src={downloadIconDataRoomHovered} alt="icon" />
                    Download All
                </div>
            </div>
        );

        const tableContent = (
            <>
                <div className={styles.TableWrapper}>
                    <table className={styles.Table}>
                        <thead className={styles.TableHead}>
                            <tr className={styles.TableHeadRow}>
                                {getHeadersFiledBy(isPDFReport).map((header, i) => (
                                    <th
                                        key={header.key}
                                        className={clsx(styles.TableHeadRowItem, {
                                            [styles.TableHeadRowItem_CaseDetails]: header.key === HEADERS.CASE_DETAILS,
                                            [styles.TableHeadRowItem_FiledAgainst]: header.key === HEADERS.FILED_AGAINST,
                                            [styles.TableHeadRowItem_FiledBy]: header.key === HEADERS.FILED_BY,
                                            [styles.TableHeadRowItem_CaseType]: header.key === HEADERS.CASE_CATEGORY,
                                            [styles.TableHeadRowItem_Date]: header.key === HEADERS.DATE,
                                            [styles.TableHeadRowItem_Report]: header.key === HEADERS.REPORTS,
                                            [styles.TableHeadRowItem_PDFReport]: isPDFReport,
                                        })}
                                        style={{
                                            whiteSpace: isPDFReport ? 'normal' : 'nowrap',
                                        }}
                                    >
                                        {header.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className={styles.TableBody} style={{ fontSize: isPDFReport ? '11px' : '' }}>
                            {legalCasesByCompanyOrDirector.length ? (
                                legalCasesByCompanyOrDirector.map((singleCase, idx) => {
                                    const uniqueKey = `${idx}-filed-by`;
                                    const legalCase = { name: singleCase.groupName };
                                    if (singleCase.status === STATUS.NO_CASES.value) {
                                        return (
                                            <tr className={styles.TableBodyRow} key={uniqueKey}>
                                                <td colSpan={getHeadersFiledBy(isPDFReport).length} className={styles.TableBodyRowItem}>
                                                    <div className={styles.NoCases}>
                                                        <img src={greenTick} height={'16px'} />
                                                        No Legal Cases filed by{' '}
                                                        {legalCase.name === 'Company'
                                                            ? convertToFirstLetterCapital(companyName)
                                                            : convertToFirstLetterCapital(legalCase.name)}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                    return (
                                        <tr
                                            className={styles.TableBodyRow}
                                            key={uniqueKey}
                                            data-lc-director={normalizeNameKey(singleCase.groupName)}
                                        >
                                            {getHeadersFiledBy(isPDFReport)?.map((header) => (
                                                <td
                                                    key={header.key}
                                                    className={clsx(styles.TableBodyRowItem, {
                                                        [styles.TableBodyRowItem_PDFReport]: isPDFReport,
                                                    })}
                                                >
                                                    {getRowItemContent(header?.key, legalCase, singleCase, uniqueKey, 'filed_by')}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr className={styles.TableBodyRow}>
                                    <td
                                        colSpan={getHeadersFiledBy(isPDFReport).length}
                                        className={clsx(styles.TableBodyRowItem, styles.Nodata)}
                                    >
                                        No data available to show
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={styles.Note}>
                    <b>*Note :</b> The cases listed above are based on an AI match of the company's legal name and its directors (please
                    verify names).
                </div>
            </>
        );

        return (
            <div className={clsx(styles.Container, { [styles.PDFReportContainer]: isPDFReport })}>
                <HeaderBodyBox title="Legal cases filed by Company / Director" headerRight={headerRight} isPDFReport={isPDFReport}>
                    {tableContent}
                </HeaderBodyBox>
            </div>
        );
    };

    const render = (
        <div className={clsx(styles.LegalCases)}>
            {renderRiskIndicators()}
            {legalCasesData?.status === DATA_FETCH_STATUS.PROCESSING ? (
                <div className={styles.Container}>
                    <HeaderBodyBox title="Legal Cases" isPDFReport={isPDFReport}>
                        <div className={styles.GeneratingData} style={{ height: '300px' }}>
                            <img src={generating} width="100px" alt="" />
                            Fetching Legal Cases Data
                            <img src={loader} alt="" width="50px" />
                        </div>
                    </HeaderBodyBox>
                </div>
            ) : (
                <>
                    {renderLegalCasesAgainstCompanyOrDirector()}
                    {renderLegalCasesByCompanyOrDirector()}
                </>
            )}
        </div>
    );

    if (isPDFReport)
        return (
            <>
                {legalCasesAgainstCompanyOrDirector?.length ? (
                    <A4Page sectionName={REPORT_SECTION_NAMES.LEGAL_CASES}>
                        <div className={styles.PDFTitle}>Legal Cases</div>
                        <AicaOverview module={'legalChecks'} isPDFReport style={{ marginBottom: '24px' }} />
                        {renderLegalCasesAgainstCompanyOrDirector()}
                    </A4Page>
                ) : (
                    <></>
                )}
                {legalCasesByCompanyOrDirector?.length ? (
                    <A4Page sectionName={REPORT_SECTION_NAMES.LEGAL_CASES}>{renderLegalCasesByCompanyOrDirector()}</A4Page>
                ) : (
                    <></>
                )}
                {renderRiskIndicators()}
            </>
        );
    else return render;
}

export default LegalCases;
